/**
 * Input sanitization utility functions
 * Provides centralized input validation and sanitization
 */

// Common HTML entities that should be escaped
const HTML_ENTITIES: { [key: string]: string } = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

/**
 * Escapes HTML entities in a string
 */
export const escapeHtml = (str: string): string => {
  return str.replace(/[&<>"'/]/g, (s) => HTML_ENTITIES[s]);
};

/**
 * Removes dangerous characters and scripts
 */
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';

  // Remove null bytes using split and filter to avoid control-regex warning
  const nullChar = String.fromCharCode(0);
  return input
    .trimStart()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/vbscript:/gi, '') // Remove vbscript: protocols
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .split(nullChar)
    .join(''); // Remove null bytes
};

/**
 * Validates and sanitizes text input with length limits
 */
export const validateAndSanitizeText = (input: string, maxLength: number, allowedPattern?: RegExp): { isValid: boolean; sanitizedValue: string; error?: string } => {
  if (!input || typeof input !== 'string') {
    return { isValid: false, sanitizedValue: '', error: 'Input is required' };
  }

  const sanitized = sanitizeInput(input);

  if (sanitized.length === 0) {
    return { isValid: false, sanitizedValue: '', error: 'Input cannot be empty after sanitization' };
  }

  if (sanitized.length > maxLength) {
    return {
      isValid: false,
      sanitizedValue: sanitized.substring(0, maxLength),
      error: `Input should not be more than ${maxLength} characters`,
    };
  }

  if (allowedPattern && !allowedPattern.test(sanitized)) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: 'Input contains invalid characters',
    };
  }

  return { isValid: true, sanitizedValue: sanitized };
};

/**
 * Validates and sanitizes numeric input
 */
export const validateAndSanitizeNumber = (input: string, maxLength: number, min?: number, max?: number): { isValid: boolean; sanitizedValue: string; error?: string } => {
  if (!input || typeof input !== 'string') {
    return { isValid: false, sanitizedValue: '', error: 'Input is required' };
  }

  const sanitized = sanitizeInput(input);

  if (sanitized.length > maxLength) {
    return {
      isValid: false,
      sanitizedValue: sanitized.substring(0, maxLength),
      error: `Input should not be more than ${maxLength} characters`,
    };
  }

  const numValue = parseFloat(sanitized);

  if (Number.isNaN(numValue)) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: 'Input must be a valid number',
    };
  }

  if (min !== undefined && numValue < min) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: `Input must be at least ${min}`,
    };
  }

  if (max !== undefined && numValue > max) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: `Input must be at most ${max}`,
    };
  }

  return { isValid: true, sanitizedValue: sanitized };
};

/**
 * Validates code input (alphanumeric and spaces only)
 */
export const validateAndSanitizeCode = (input: string, maxLength: number): { isValid: boolean; sanitizedValue: string; error?: string } => {
  const codePattern = /^[A-Za-z0-9\s]+$/;
  return validateAndSanitizeText(input, maxLength, codePattern);
};

/**
 * Validates name input (letters, numbers, spaces, and hyphens only)
 */
export const validateAndSanitizeName = (input: string, maxLength: number): { isValid: boolean; sanitizedValue: string; error?: string } => {
  const namePattern = /^[A-Za-z0-9\s-]+$/;
  return validateAndSanitizeText(input, maxLength, namePattern);
};

/**
 * Validates name input allowing letters (A–Z, a–z), digits (0–9), spaces, hyphens,
 * and the special characters: ! @ # $ % ^ & * ( ) _ + = [ ] { } ; : ' " , . ` ~
 *
 * Note: Characters <, >, /, and \ are intentionally disallowed to prevent XSS vectors.
 *
 * @param input - The raw input string to validate and sanitize.
 * @param maxLength - The maximum allowed length of the input.
 * @returns An object indicating whether the input is valid, the sanitized value, and an optional error message.
 */
export const validateAndSanitizeNameWithSpecialCharacter = (input: string, maxLength: number): { isValid: boolean; sanitizedValue: string; error?: string } => {
  // Allow letters, numbers, spaces, hyphens, and common special characters
  // Disallow <, >, /, and \ to prevent XSS vectors
  const namePattern = /^[A-Za-z0-9\s-!@#$%^&*()_+=[\]{};:'",.`~]+$/;
  return validateAndSanitizeText(input, maxLength, namePattern);
};

/**
 * Validates name input with a limited set of special characters for sub-category names.
 * Allows: letters, numbers, spaces (non-consecutive), and: & . , ' _ / - ( ) ? @
 *
 * SECURITY NOTE: Forward slash (/) is intentionally allowed in this function despite being
 * disallowed in validateAndSanitizeNameWithSpecialCharacter. This is safe because:
 * 1. The input is passed through sanitizeInput() which removes all HTML tags, script tags,
 *    and dangerous protocols (javascript:, vbscript:) before pattern validation
 * 2. Sub-category names are used in controlled contexts (database storage, display in UI)
 *    and are not directly interpolated into URLs, file paths, or HTML attributes
 * 3. Forward slash is a common business requirement for sub-category names (e.g., "Sales/Marketing", "Process Improvement/ Innovation")
 * 4. The pattern restricts input to a whitelist of safe characters, preventing other XSS vectors
 */
export const validateAndSanitizeNameWithLimitedSpecialCharacters = (input: string, maxLength: number): { isValid: boolean; sanitizedValue: string; error?: string } => {
  // Allow letters, numbers, single spaces (non-consecutive), and the following special characters: & . , ' _ / - ( ) ? @
  // Must start with non-space, allows single spaces anywhere, optional trailing space
  const namePattern = /^[A-Za-z0-9&.,'_/\-()?@]+(?: [A-Za-z0-9&.,'_/\-()?@]+)* ?$/;
  return validateAndSanitizeText(input, maxLength, namePattern);
};

/**
 * Sanitizes HTML content from rich text editor
 */
export const sanitizeRichText = (input: string): string => {
  if (!input || typeof input !== 'string') return '';

  // Allow basic HTML tags for rich text but sanitize dangerous content
  // Remove null bytes using split and filter to avoid control-regex warning
  const nullChar = String.fromCharCode(0);
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/vbscript:/gi, '') // Remove vbscript: protocols
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .split(nullChar)
    .join(''); // Remove null bytes
};

/**
 * Validates array input (selections from dropdowns)
 */
export const validateArraySelection = (input: any[], fieldName: string, isRequired = true): { isValid: boolean; error?: string } => {
  if (!Array.isArray(input)) {
    return { isValid: false, error: `${fieldName} must be an array` };
  }

  if (isRequired && input.length === 0) {
    return { isValid: false, error: `Please select at least one ${fieldName.toLowerCase()}` };
  }

  // Validate each item in the array has required properties
  const invalidItem = input.find((item) => !item || typeof item !== 'object' || !item.id || !item.label);
  if (invalidItem) {
    return { isValid: false, error: `Invalid ${fieldName.toLowerCase()} selection` };
  }

  return { isValid: true };
};
